import styled from 'styled-components';
import kebabCase from 'lodash/kebabCase';
import Outlets from 'components/Outlets';
import { ThirdPartyLinksTypeQuery } from 'constant/constants';
import { sortOutlets } from 'utils/common';
import { useEffect, useState, useMemo, ChangeEvent } from 'react';
import usePosition from 'hooks/usePosition';
import Header from 'components/Header';
import PageContainer from 'components/PageContainer';
import MetaHeader from 'components/MetaHeader';
import { getMicrositeByOutletDetails } from 'services';
import PoweredByMomos from 'components/PoweredByMomos';
import { Brand, MicrositeConfig, Outlet } from 'utils/types';
import { GetServerSideProps } from 'next';
import TextInput from 'components/TextInput';

interface FoodDeliveryCategoryProps {
  category: string;
  brandDetails: Brand;
  micrositeConfig: MicrositeConfig;
  data: Outlet[];
}

export const FILTER_LOCATION = (outlets: Outlet[] = [], search: string) => {
  if (!search) return outlets;
  const searchLowerCase = search.toLowerCase();

  return outlets?.filter((outlet: Outlet) => {
    if (outlet?.name?.toLowerCase()?.includes(searchLowerCase)) return true;
    if (outlet?.address1?.toLowerCase()?.includes(searchLowerCase)) return true;
    if (outlet?.address2?.toLowerCase()?.includes(searchLowerCase)) return true;
    if (outlet?.city?.toLowerCase()?.includes(searchLowerCase)) return true;
    if (outlet?.state?.toLowerCase()?.includes(searchLowerCase)) return true;
  });
};

export default function FoodDeliveryCategory({
  data,
  category,
  brandDetails,
  micrositeConfig,
}: FoodDeliveryCategoryProps) {
  const [locationsData, setLocationsData] = useState(data);
  const [search, setSearch] = useState<string>('');

  const { latLong }: any = usePosition();
  useEffect(() => {
    if (latLong) {
      const sortOutletsData = sortOutlets(latLong, data);
      setLocationsData(sortOutletsData);
    }
  }, [data, latLong]);

  const onChangeSearch = (evt: ChangeEvent<HTMLInputElement>) => {
    setSearch(evt.target.value);
  };

  const filterLocationData: Outlet[] = useMemo(() => {
    return FILTER_LOCATION(locationsData, search);
  }, [search, locationsData]);

  return (
    <PageWrap>
      <MetaHeader
        title="Order for Delivery"
        image={brandDetails?.logoUrl ? brandDetails.logoUrl : undefined}
        pixelId={micrositeConfig?.micrositePixelId}
      />
      <Header
        brandDetails={brandDetails}
        theme={micrositeConfig?.micrositeTheme}
        hideMenuLink={!micrositeConfig?.menuFlag}
      />
      <PageContainer>
        <div className="mb-20">{`Order on ${category}`}</div>
        <h2 className="heading-1 mb-30">Select A Location</h2>
        <TextInput placeholder="Search by address, city or zip code" onChange={onChangeSearch} className="mb-20" />
        <Outlets data={filterLocationData} category={category} minHeight={'220px'} />
        <PoweredByMomos />
      </PageContainer>
    </PageWrap>
  );
}

export const getServerSideProps: GetServerSideProps = async ({ params, req }) => {
  try {
    const data = await getMicrositeByOutletDetails(req, params?.slug, {
      type: ThirdPartyLinksTypeQuery.FOOD_DELIVERY,
      category: kebabCase(params?.category as string),
    });

    return {
      props: {
        data: data.outlets || [],
        slug: params?.slug,
        category: params?.category,
        brandDetails: data.brandDetails || null,
        micrositeConfig: data.micrositeConfig || null,
      },
    };
  } catch (err) {
    return { notFound: true };
  }
};

const PageWrap = styled.div`
  .options-wrap {
    margin-bottom: 30px;
    margin-top: 30px;

    .option-item {
      align-items: center;
      justify-content: center;
      display: flex;
      cursor: pointer;
      border: 1px solid var(--grey-light);
      height: 140px;
      border-radius: 10px;

      &.active-item {
        background: var(--orange);
      }

      .menu-img {
        object-fit: contain;
      }
    }
  }
`;
